// src/components/BookingModal/BookingModal.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { motion, AnimatePresence } from "framer-motion";
import { X, Calendar, Music2, MapPin, ListChecks } from "lucide-react";
import NeonButton from "../ResponsiveButton/NeonButton.jsx";
import { useTheme } from "../../context/ThemeContext";

function todayStr() {
  return new Date().toISOString().split("T")[0];
}

export default function BookingModal({
  open,
  onClose,
  onApply,
  artistTypes = [],
  genres = [],
  services = [],
  regions = [],
  initialDate,
  isWelcome = false,
}) {
  const { theme } = useTheme();
  const [step, setStep] = useState(0);
  const [date, setDate] = useState(initialDate || todayStr());
  const [type, setType] = useState("");
  const [pickedTags, setPickedTags] = useState([]);   // genret/palvelut
  const [pickedRegions, setPickedRegions] = useState([]);

  const MAX_TAGS = 6;
    const [tagsOpen, setTagsOpen] = useState(false);

    const toggleTag = useCallback((val) => {
      setPickedTags((prev) =>
        prev.includes(val)
          ? prev.filter((v) => v !== val)
          : prev.length < MAX_TAGS
          ? [...prev, val]
          : prev
      );
    }, []);

  const resetForm = useCallback(() => {
    setStep(0);
    setDate(initialDate || todayStr());
    setType("");
    setPickedTags([]);
    setPickedRegions([]);
  }, [initialDate]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    resetForm();
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open, resetForm]);

  useEffect(() => {
    if (initialDate) setDate(initialDate);
  }, [initialDate]);

const isCompany = (type || "").toLowerCase() === "yritys";
const tagLabel = isCompany ? "Palvelut" : "Musiikkityyli";
const tagHelp = "Valitse yksi tai useampi.";

// salli sekä string[] että object[] (id/slug/name/label/value)
const toOptions = (raw = []) =>
  (Array.isArray(raw) ? raw : [])
    .map((item) => {
      if (typeof item === "string") return { value: item, label: item };
      const label = String(
        item.name ?? item.label ?? item.title ?? item.slug ?? item.value ?? ""
      ).trim();
      const value = String(item.slug ?? item.value ?? item.id ?? label).trim();
      if (!label || !value) return null;
      return { value, label };
    })
    .filter(Boolean);

// Normalisoi propit yhtenäiseen muotoon
const genreOptions   = useMemo(() => toOptions(genres),   [genres]);
const serviceOptions = useMemo(() => toOptions(services), [services]);
const regionOptions  = useMemo(() => toOptions(regions),  [regions]);

// Käytettävät tagit tyypin perusteella
const tagOptions = isCompany ? serviceOptions : genreOptions;



  const steps = useMemo(
    () => [
      { key: "date",   label: "Ajankohta",     icon: Calendar,
        help: "Valitse päivämäärä. Näytämme vain vapaat toimijat (joilla ei ole aktiivista varausta tai avointa kyselyä)." },
      { key: "type",   label: "Tyyppi",        icon: Music2,
        help: "Etsitkö esiintyjää vai yrityspalvelua?" },
      { key: "tags",   label: tagLabel,        icon: ListChecks,
        help: tagHelp },
      { key: "region", label: "Missä?",        icon: MapPin,
        help: "Valitse maakunta, jossa tapahtuma järjestetään tai palvelua tarvitaan." },
    ],
    [tagHelp, tagLabel]
  );


  const canNext = () => {
    if (step === 0) return !!date;
    if (step === 1) return !!type;
    return true;
  };

  const toggleChip = (value, arr, setArr) => {
    setArr(arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value]);
  };

  const finish = () => {
    onApply?.({
      date,
      types: type ? [type] : [],
      genres: isCompany ? [] : pickedTags,
      services: isCompany ? pickedTags : [],
      regions: pickedRegions,
    });
    onClose?.();
  };

 // 🔴 Live-esikatselu: päivitä haku taustalla kun valinnat muuttuvat
 useEffect(() => {
   if (!open) return;
   const t = setTimeout(() => {
     onApply?.({
       date,
       types: type ? [type] : [],
       genres: isCompany ? [] : pickedTags,
       services: isCompany ? pickedTags : [],
       regions: pickedRegions,
       _preview: true,          // <-- kertoo ettei modalia suljeta
     });
   }, 150); // kevyt debounce, ettei tulvi
   return () => clearTimeout(t);
 }, [open, date, type, pickedTags, pickedRegions, isCompany, onApply]);

  const ctaText = isCompany
    ? "🚀 Näytä sopivat yritykset"
    : type
    ? "🚀 Näytä sopivat esiintyjät"
    : "🚀 Näytä sopivat vaihtoehdot";

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Overlay (sulje vain kun klikataan overlayta, ei dialogia) */}
          <div
            className="absolute inset-0 z-0 bg-black/90"
            onClick={onClose}
            aria-hidden="true"
            role="presentation"
          />

          {/* Dialog */}
          <motion.div
            className={`
              relative z-[101]
              w-[94%] sm:w-[90%]
              max-w-[680px] xl:max-w-[720px]
              max-h-[92vh] overflow-y-auto overflow-x-hidden
              rounded-2xl ring-1 backdrop-blur-sm p-0
              ${theme === "dark" ? "bg-night-700/80 ring-night-500/50" : "bg-lemon/90 ring-black/10"}
            `}
            initial={{ y: 40, scale: 0.98, opacity: 0 }}
            animate={{ y: 0, scale: 1, opacity: 1 }}
            exit={{ y: 20, scale: 0.98, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 26 }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="booking-modal-title"
              // Estetään dialogin sisäisten klikkien kuplaaminen overlaylle
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {/* Close button (absolute, yläoikea) */}
            <button
              onClick={onClose}
              className="absolute top-2 right-2 sm:top-3 sm:right-3 inline-flex h-9 w-9 sm:h-10 sm:w-10 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-black dark:text-white hover:bg-white/10"
              aria-label="Sulje"
              type="button"
            >
              <X className="h-4 w-4 sm:h-5 sm:w-5" />
            </button>

            {/* Header */}
            <div className="w-full px-4 sm:px-5 pt-4 pr-14 sm:pr-16">
              <h3
                id="booking-modal-title"
                className="font-customFont2 text-2xl sm:text-2xl dark:text-pink/80 text-black/80 font-bold"
              >
                {isWelcome ? "Tervetuloa Buukkeriin!" : "Pikahaku"}
              </h3>
              <p className="dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm">
                {isWelcome
                  ? "Vastaa muutamaan kysymykseen ja näet heti vapaat toimijat."
                  : "Vastaa neljään nopeaan kysymykseen ja löydät sopivat vaihtoehdot."}
              </p>
            </div>

            {/* Stepper */}
            <div className="flex flex-wrap sm:flex-nowrap items-center gap-2 px-4 sm:px-5 pt-3">
              {steps.map((s, i) => {
                const Icon = s.icon;
                const active = i === step;
                const upcoming = i > step;
                const hideBecauseNoType = s.key === "tags" && !type && !active;

                if (hideBecauseNoType) return null; // piilota “Musiikkityyli/Palvelut” kun tyyppiä ei vielä valittu

                return (
                  <div key={s.key} className="flex items-center gap-2 shrink-0">
                    <div
                      className={`inline-flex items-center gap-2 rounded-full px-2.5 py-1 ring-1
                        ${active ? "bg-pink/80 ring-primary/40" : "bg-white/50 ring-white/10"}
                        ${upcoming ? "opacity-40 pointer-events-none" : ""}
                      `}
                      aria-disabled={upcoming}
                    >
                      <Icon
                        className={`h-3.5 w-3.5 sm:h-4 sm:w-4 ${active ? "text-white" : "text-black/70"}`}
                      />
                      <span
                        className={`dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm ${active ? "text-white" : "text-black/80"}`}
                      >
                        {s.label}
                      </span>
                    </div>
                    {i < steps.length - 1 && <div className="h-px w-4 sm:w-6 bg-white/10" />}
                  </div>
                );
              })}
            </div>

            {/* Help text */}
            <div className="px-4 sm:px-5 pt-5">
              <p className="dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm">
                {steps[step]?.help}
              </p>
            </div>

            {/* Content */}
            <div className="px-4 sm:px-5 py-4 sm:py-5">
              {/* Step 0: Päivämäärä */}
              {step === 0 && (
                <div className="space-y-2">
                  <label className="block dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm mb-1">
                    Päivämäärä:
                  </label>
                  <input
                    type="date"
                    value={date}
                    min={todayStr()}
                    onChange={(e) => setDate(e.target.value)}
                    className="w-full h-11 rounded-xl px-3 bg-white/10 ring-1 ring-white/15 focus:ring-2 focus:ring-primary/60 dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm"
                  />
                </div>
              )}

              {/* Step 1: Tyyppi */}
              {step === 1 && (
                <div>
                  <label className="block dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm mb-2">
                    Tyyppi:
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {artistTypes.map((t) => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => { setType(type === t ? "" : t); setPickedTags([]); }}
                        className={`px-3 py-1.5 rounded-full text-sm ring-1
                          ${type === t ? "bg-primary/80 ring-pink/50 text-black" : "bg-pink/80 ring-white/15 text-white/85 hover:bg-electric/60"}`}
                        aria-pressed={type === t}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                  {!type && (
                    <p className="mt-2 dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm">Valitse ensin tyyppi jatkaaksesi.</p>
                  )}
                </div>
              )}

              {step === 2 && (
                <div>
                  <label className="block dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm mb-2">
                    {tagLabel}:
                  </label>

                  {/* Avaa/sulje lista */}
                  <button
                    type="button"
                    className="w-full justify-start rounded-full px-3 py-2 ring-1 ring-black/10 dark:ring-white/15
                              bg-white/80 dark:bg-white/10 hover:bg-white dark:hover:bg-white/15
                              text-sm transition text-black/80 dark:text-white/80"
                    onClick={() => setTagsOpen((o) => !o)}
                  >
                    {tagsOpen
                      ? "Sulje lista"
                      : `Valitse ${isCompany ? "palveluja" : "tyylejä"}…`}
                  </button>

                  {/* Lista (scrollattava) */}
                  {tagsOpen && (
                    <div
                      className="mt-2 max-h-60 overflow-auto w-full rounded-lg
                                bg-white dark:bg-black/90
                                border border-black/10 dark:border-white/10 shadow
                                text-black/80 dark:text-white/80"
                    >
                      {tagOptions.length ? (
                        tagOptions.map((opt) => {
                          const checked = pickedTags.includes(opt.value);
                          const disabled = !checked && pickedTags.length >= MAX_TAGS;
                          return (
                            <label
                              key={opt.value}
                              className={`flex items-center gap-2 px-3 py-2 cursor-pointer
                                          hover:bg-black/5 dark:hover:bg-white/10
                                          ${disabled ? "opacity-50" : ""}`}
                            >
                              <input
                                type="checkbox"
                                className="h-4 w-4 accent-pink-600 dark:accent-pink-400"
                                checked={checked}
                                disabled={disabled}
                                onChange={() => toggleTag(opt.value)}
                              />
                              <span className="text-inherit">{opt.label}</span>
                            </label>
                          );
                        })
                      ) : (
                        <div className="px-3 py-2 opacity-80 text-black/80 dark:text-white/80">
                          {isCompany
                            ? "Ei palveluoptioita saatavilla."
                            : "Ei genreoptioita saatavilla."}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Laskuri */}
                  <p className="text-xs mt-1 text-black/70 dark:text-white/70">
                    {pickedTags.length}/{MAX_TAGS} valittu
                  </p>

                  {/* Valitut chippeinä listan alla */}
                  <div className="my-3 flex flex-wrap gap-2">
                    {pickedTags.length ? (
                      pickedTags.map((val) => {
                        const opt = tagOptions.find((o) => o.value === val);
                        const label = opt?.label ?? val;
                        return (
                          <span
                            key={val}
                            className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs
                                      ring-1 ring-pink/40 bg-pink/80 text-white"
                          >
                            <span>{label}</span>
                            <button
                              type="button"
                              aria-label="Poista"
                              onClick={() => toggleTag(val)}
                              className="ml-1 inline-flex items-center justify-center rounded-full h-4 w-4
                                        bg-black/20 hover:bg-black/30"
                            >
                              ×
                            </button>
                          </span>
                        );
                      })
                    ) : (
                      <span className="text-black/70 dark:text-white/70">
                        Ei valintoja vielä.
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Step 3: Alueet */}
              {step === 3 && (
                <div>
                  <label className="block dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm mb-2">
                    Alueet:
                  </label>
                    <div className="flex flex-wrap gap-2">
                      {regionOptions.map((r) => (
                        <button
                          key={r.value}
                          type="button"
                          onClick={() => toggleChip(r.value, pickedRegions, setPickedRegions)}
                          className={`px-3 py-1.5 rounded-full text-sm ring-1
                            ${pickedRegions.includes(r.value)
                              ? "bg-primary/30 ring-primary/60 text-black/90 dark:text-white/90"
                              : "bg-pink/80 ring-white/15 text-white/90 hover:bg-electric/60"}`}
                          aria-pressed={pickedRegions.includes(r.value)}
                        >
                          {r.label}
                        </button>
                      ))}
                      {regionOptions.length === 0 && (
                        <span className="opacity-80">Ei alueita saatavilla.</span>
                      )}
                    </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between px-4 sm:px-5 py-3 sm:py-4 border-t border-white/10 gap-3 sm:gap-0">
              <div className="dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm font-bold text-center sm:text-left">
                Vaihe {step + 1} / {steps.length}
              </div>
              <div className="flex flex-col sm:flex-row items-center gap-2 w-full sm:w-auto">
                {step > 0 && (
                  <NeonButton
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => setStep(step - 1)}
                    className="w-full sm:w-auto"
                  >
                    Takaisin
                  </NeonButton>
                )}
                {step < steps.length - 1 ? (
                  <NeonButton
                    type="button"
                    variant="primary"
                    size="sm"
                    onClick={() => canNext() && setStep(step + 1)}
                    disabled={!canNext()}
                    className="w-full sm:w-auto"
                  >
                    Seuraava
                  </NeonButton>
                ) : (
                  <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: "spring", stiffness: 300, damping: 15 }}
                    className="w-full sm:w-auto"
                  >
                    <NeonButton
                      type="button"
                      variant="primary"
                      size="sm"
                      onClick={finish}
                      className="w-full sm:w-auto hover:scale-105 hover:shadow-[0_0_25px_rgba(255,20,147,0.8)] transition duration-300"
                    >
                      {ctaText}
                    </NeonButton>
                  </motion.div>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

BookingModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onApply: PropTypes.func,
  artistTypes: PropTypes.arrayOf(PropTypes.string),
  genres: PropTypes.arrayOf(PropTypes.string),
  services: PropTypes.arrayOf(PropTypes.string),
  regions: PropTypes.arrayOf(PropTypes.string),
  initialDate: PropTypes.string,
  isWelcome: PropTypes.bool,
};

BookingModal.defaultProps = {
  artistTypes: [],
  genres: [],
  services: [],
  regions: [],
  isWelcome: false,
};
